import { warn } from "./config.js";
import type { HookCtx, Item, RenderHookCtx, SlaterHooks } from "./types.js";

/**
 * Per-project hook registry, keyed by block key (`"*"` = every block). Written
 * through hooks/register.ts (the config object, the pre-boot queue, or the live
 * `window.SlaterSearch` API); read here at invocation time — never copied into
 * a resolved ListConfig — so a hook registered after boot is live on the next
 * pipeline run without re-resolving anything.
 */
const registry = new Map<string, SlaterHooks>();

const HOOK_NAMES = ["transformItems", "onItemRender", "onRender"] as const;

/**
 * Validates and stores hooks for a key, shallow-merging over any prior
 * registration — later wins per hook name, so separate scripts can each
 * contribute hooks to the same block. Unknown names are ignored WITHOUT a
 * warning: bundles are immutable-cached for a year and must stay quiet about
 * hook names invented after they were built. Returns true when at least one
 * hook was stored.
 */
export function registerHooks(key: unknown, hooks: unknown): boolean {
  if (typeof key !== "string" || !key) {
    warn("hooks: the block key must be a non-empty string.");
    return false;
  }
  if (typeof hooks !== "object" || hooks === null) {
    warn(`hooks "${key}": expected an object of hook functions.`);
    return false;
  }

  const stored: SlaterHooks = { ...registry.get(key) };
  let storedAny = false;
  for (const name of HOOK_NAMES) {
    const value = (hooks as Record<string, unknown>)[name];
    if (value === undefined) continue;
    if (typeof value !== "function") {
      warn(`hooks "${key}": ${name} is not a function — ignored.`);
      continue;
    }
    (stored as Record<string, unknown>)[name] = value;
    storedAny = true;
  }
  if (storedAny) registry.set(key, stored);
  return storedAny;
}

/** Effective hooks for a block: `"*"` first, block-specific overrides by name. */
function hooksFor(key: string): SlaterHooks | undefined {
  const all = registry.get("*");
  const own = registry.get(key);
  if (!all && !own) return undefined;
  return { ...all, ...own };
}

/**
 * Runs a project's transformItems over one page of items. The caller hands in a
 * copy of the response array (the datasource caches responses by reference). A
 * throw or a non-array return — an accidental `async` hook returns a Promise —
 * degrades to the original items with a warning: a broken hook must never break
 * the block. `Array.isArray`, not instanceof — the hook may live in another
 * realm.
 */
export function runTransformItems(items: Item[], ctx: HookCtx): Item[] {
  const hook = hooksFor(ctx.key)?.transformItems;
  if (!hook) return items;
  try {
    const result = hook(items, ctx);
    if (!Array.isArray(result)) {
      warn(`hooks "${ctx.key}": transformItems must return an array — ignored.`);
      return items;
    }
    return result;
  } catch (error) {
    warn(`hooks "${ctx.key}": transformItems threw — ignored.`, error);
    return items;
  }
}

/** Invokes onItemRender on one card; a throw leaves the card as bound. */
export function runItemRender(card: Element, item: Item, ctx: HookCtx): void {
  const hook = hooksFor(ctx.key)?.onItemRender;
  if (!hook) return;
  try {
    hook(card, item, ctx);
  } catch (error) {
    warn(`hooks "${ctx.key}": onItemRender threw — card left as bound.`, error);
  }
}

/** Invokes onRender after a page settled; a throw changes nothing. */
export function runRender(ctx: RenderHookCtx): void {
  const hook = hooksFor(ctx.key)?.onRender;
  if (!hook) return;
  try {
    hook(ctx);
  } catch (error) {
    warn(`hooks "${ctx.key}": onRender threw.`, error);
  }
}
